/**
 * Cookie Consent Utility
 * Manages user consent preferences for GDPR/privacy compliance
 */

// Consent levels
export const CONSENT_LEVELS = {
  NONE: 'none',
  ESSENTIAL: 'essential',
  ALL: 'all',
};

const CONSENT_KEY = 'customsync_cookie_consent';
const CONSENT_DATE_KEY = 'customsync_cookie_consent_date';

/**
 * Get the user's current consent level
 */
export const getConsent = () => {
  if (typeof window === 'undefined') return CONSENT_LEVELS.NONE;
  
  try {
    return localStorage.getItem(CONSENT_KEY) || CONSENT_LEVELS.NONE;
  } catch (error) {
    console.error('Error reading consent:', error);
    return CONSENT_LEVELS.NONE;
  }
};

/**
 * Set the user's consent level
 */
export const setConsent = (level) => {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(CONSENT_KEY, level);
    localStorage.setItem(CONSENT_DATE_KEY, new Date().toISOString());
    
    // Trigger a custom event so other parts of the app can react
    window.dispatchEvent(new CustomEvent('consentChanged', { detail: { level } }));
  } catch (error) {
    console.error('Error setting consent:', error);
  }
};

/**
 * Check if analytics is allowed
 */
export const isAnalyticsAllowed = () => {
  const consent = getConsent();
  return consent === CONSENT_LEVELS.ALL;
};

/**
 * Check if the user has made a consent choice
 */
export const hasConsent = () => {
  const consent = getConsent();
  return consent !== CONSENT_LEVELS.NONE;
};

/**
 * Clear consent (for testing or user request)
 */
export const clearConsent = () => {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.removeItem(CONSENT_KEY);
    localStorage.removeItem(CONSENT_DATE_KEY);
    window.dispatchEvent(new CustomEvent('consentChanged', { detail: { level: CONSENT_LEVELS.NONE } }));
  } catch (error) {
    console.error('Error clearing consent:', error);
  }
};

/**
 * Get when consent was given
 */
export const getConsentDate = () => {
  if (typeof window === 'undefined') return null;
  
  try {
    const date = localStorage.getItem(CONSENT_DATE_KEY);
    return date ? new Date(date) : null;
  } catch (error) {
    console.error('Error reading consent date:', error);
    return null;
  }
};

